// JOBE-0164.2: FleetQualityStrip — five fleet-level KPI tiles with
// deviation-mode sparks (D11). Each tile's number is a link to a
// drill target (D10): in-page anchors for tiles 2-5, /organization
// for tile 1.

import { useNavigate } from 'react-router-dom';
import { KpiTile } from '../components/KpiTile/KpiTile.jsx';
import { Spark } from '../components/Spark/Spark.jsx';
import './FleetQualityStrip.css';

// Deterministic 7-day rolling series derived per-metric from the current
// fixture value. The v1 fixture (JOBE-0157.4) does not carry per-tile
// spark series; this helper produces a defensible mock until a real
// endpoint lands. The series ends with the current value.
export function deriveSparkSeries(metric, fleet) {
  if (!fleet) return [0, 0, 0, 0, 0, 0, 0];
  switch (metric) {
    case 'agents': {
      const n = fleet.agentCount ?? 0;
      return [n, n, n, n, n, n, n];
    }
    case 'passRate': {
      const p = fleet.evalPassRate ?? 0;
      return [
        +(p * 1.05).toFixed(4),
        +(p * 1.04).toFixed(4),
        +(p * 1.03).toFixed(4),
        +(p * 1.02).toFixed(4),
        +(p * 1.01).toFixed(4),
        +(p * 1.005).toFixed(4),
        p,
      ];
    }
    case 'failures': {
      const c = sumFailures(fleet);
      return [
        Math.round(c * 0.7),
        Math.round(c * 0.75),
        Math.round(c * 0.8),
        Math.round(c * 0.85),
        Math.round(c * 0.9),
        Math.round(c * 0.95),
        c,
      ];
    }
    case 'p50': {
      const v = meanLatency(fleet);
      return [
        Math.round(v * 0.92),
        Math.round(v * 0.94),
        Math.round(v * 0.96),
        Math.round(v * 0.98),
        Math.round(v * 1.0),
        Math.round(v * 1.01),
        v,
      ];
    }
    case 'corrections': {
      const c = fleet.recentCorrections?.length ?? 0;
      // Series stays small; nudge for plausible variability around c.
      return [
        Math.max(0, c - 1),
        c,
        Math.max(0, c - 1),
        c,
        Math.max(0, c + 1),
        c,
        c,
      ];
    }
    default:
      return [0, 0, 0, 0, 0, 0, 0];
  }
}

function sumFailures(fleet) {
  if (!fleet?.failureClasses) return 0;
  return fleet.failureClasses.reduce((acc, fc) => acc + (fc.count ?? 0), 0);
}

function meanLatency(fleet) {
  const xs = fleet?.agents?.map((a) => a.p50LatencyMs).filter((n) => typeof n === 'number') ?? [];
  if (xs.length === 0) return 480;
  return Math.round(xs.reduce((a, b) => a + b, 0) / xs.length);
}

function passRateTone(rate) {
  return rate < 0.90 ? 'warn' : 'ok';
}

function failureCountTone(count) {
  if (count > 25) return 'bad';
  if (count > 10) return 'warn';
  return 'ok';
}

function scrollToAnchor(id) {
  if (typeof document === 'undefined') return;
  const el = document.getElementById(id);
  if (el && typeof el.scrollIntoView === 'function') {
    el.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }
}

/**
 * @param {object} props
 * @param {object} props.fleet  loaded `promptops.mock.fleet` fixture
 */
export function FleetQualityStrip({ fleet }) {
  const navigate = useNavigate();
  if (!fleet) return null;

  const agentCount = fleet.agentCount ?? 0;
  const passRate = fleet.evalPassRate ?? 0;
  const failures = sumFailures(fleet);
  const p50 = meanLatency(fleet);
  const corrections = fleet.recentCorrections?.length ?? 0;

  const passTone = passRateTone(passRate);
  const failTone = failureCountTone(failures);

  return (
    <section className="fleet-quality-strip" aria-label="Fleet quality strip">
      <KpiTile
        label="TOTAL AGENTS"
        value={agentCount}
        tone="ok"
        spark={
          <Spark
            mode="deviation"
            tone="ok"
            data={deriveSparkSeries('agents', fleet)}
            ariaLabel="Total agents, last 7 days"
          />
        }
        onClick={() => navigate('/organization')}
      />
      <KpiTile
        label="FLEET PASS RATE"
        value={Math.round(passRate * 100)}
        unit="%"
        tone={passTone}
        spark={
          <Spark
            mode="deviation"
            tone={passTone}
            data={deriveSparkSeries('passRate', fleet)}
            ariaLabel="Fleet pass rate, last 7 days"
          />
        }
        onClick={() => scrollToAnchor('agent-ranking')}
      />
      <KpiTile
        label="FAILURES THIS WEEK"
        value={failures}
        tone={failTone}
        spark={
          <Spark
            mode="deviation"
            tone={failTone}
            data={deriveSparkSeries('failures', fleet)}
            ariaLabel="Failures this week, last 7 days"
          />
        }
        onClick={() => scrollToAnchor('failure-class-index')}
      />
      <KpiTile
        label="P50 LATENCY"
        value={p50}
        unit="ms"
        tone="ok"
        spark={
          <Spark
            mode="deviation"
            tone="ok"
            data={deriveSparkSeries('p50', fleet)}
            ariaLabel="P50 latency, last 7 days"
          />
        }
        onClick={() => scrollToAnchor('agent-ranking')}
      />
      <KpiTile
        label="RECENT CORRECTIONS"
        value={corrections}
        tone="ok"
        spark={
          <Spark
            mode="deviation"
            tone="ok"
            data={deriveSparkSeries('corrections', fleet)}
            ariaLabel="Recent corrections, last 7 days"
          />
        }
        onClick={() => scrollToAnchor('recent-corrections')}
      />
    </section>
  );
}

export default FleetQualityStrip;
